/**
 * Request and response shapes for both halves of the API.
 *
 * The API validates against these, the admin panel infers its client types from
 * them, and the Kotlin app mirrors the `/device/*` ones by hand.
 */

import { z } from 'zod';
import {
  hhMmSchema,
  manifestScreenSchema,
  openingHoursEntrySchema,
  playlistItemSchema,
  rotationDegreesSchema,
} from './manifest.js';
import { isValidTimeZone } from './time.js';

// ---------------------------------------------------------------------------
// Admin authentication
// ---------------------------------------------------------------------------

export const emailSchema = z.string().email().max(254).toLowerCase().trim();

export const otpRequestSchema = z.object({
  email: emailSchema,
  turnstileToken: z.string().min(1),
});

export const otpVerifySchema = z.object({
  email: emailSchema,
  code: z.string().regex(/^\d{6}$/),
});

export const userRoleSchema = z.enum(['admin', 'manager']);

export const sessionUserSchema = z.object({
  email: emailSchema,
  name: z.string().default(''),
  role: userRoleSchema,
  /** Empty for admins, who reach every store. */
  storeIds: z.array(z.string()).default([]),
  notifyOffline: z.boolean().default(true),
});

export const otpVerifyResponseSchema = z.object({
  token: z.string(),
  expiresAt: z.string().datetime(),
  user: sessionUserSchema,
});

// ---------------------------------------------------------------------------
// Device: pairing
//
// Deliberately split like RFC 8628's device flow. `userCode` is public — it is
// on a screen facing customers — and only lets an admin claim the device.
// `deviceSecret` never leaves the init response, so photographing the pairing
// screen gets you nothing.
// ---------------------------------------------------------------------------

export const deviceInfoSchema = z.object({
  model: z.string().max(120),
  manufacturer: z.string().max(120).default(''),
  osVersion: z.string().max(60),
  sdkInt: z.number().int().positive(),
  appVersionCode: z.number().int().nonnegative(),
  appVersionName: z.string().max(40).default(''),
  screenWidthPx: z.number().int().positive(),
  screenHeightPx: z.number().int().positive(),
});

export const pairInitRequestSchema = z.object({
  deviceInfo: deviceInfoSchema,
});

export const pairInitResponseSchema = z.object({
  userCode: z.string(),
  deviceSecret: z.string(),
  pollIntervalSec: z.number().int().positive(),
  expiresAt: z.string().datetime(),
});

export const pairPollQuerySchema = z.object({
  userCode: z.string().min(1),
  deviceSecret: z.string().min(1),
});

/**
 * Polling has two success shapes, and which one arrived is told by the STATUS,
 * not by anything in the body.
 *
 *   202  still waiting to be claimed -> `pairPollPendingResponseSchema`
 *   200  claimed, and this is the one and only delivery of the token
 *   404  the code is gone: expired, burned by too many wrong secrets, or already
 *        collected. Start again from `pair/init`.
 *
 * This is written down because the pending answer is overwhelmingly the common
 * one — a device polls for as long as it takes somebody to walk to a laptop — and
 * a client that decoded every 2xx as the token shape would fail on the ordinary
 * path and work only in a demo.
 */
export const pairPollPendingResponseSchema = z.object({
  status: z.literal('pending'),
  pollIntervalSec: z.number().int().positive(),
});

export const pairPollResponseSchema = z.object({
  deviceToken: z.string(),
  screenId: z.string(),
  screenName: z.string(),
  storeName: z.string(),
});

// ---------------------------------------------------------------------------
// Device: heartbeat, commands, playback
// ---------------------------------------------------------------------------

export const deviceErrorCodeSchema = z.enum([
  'download_failed',
  'hash_mismatch',
  'storage_full',
  'content_set_exceeds_storage',
  'playback_error',
  'manifest_invalid',
  'update_failed',
]);

export const deviceErrorSchema = z.object({
  code: deviceErrorCodeSchema,
  assetId: z.string().nullable().default(null),
  detail: z.string().max(500).default(''),
  at: z.string().datetime(),
});

export const playbackStateSchema = z.enum([
  'playing',
  'closed',
  'idle',
  'pairing',
  'updating',
]);

export const updateStateSchema = z.enum([
  'none',
  'downloading',
  'staged',
  'awaiting-confirmation',
  'installing',
]);

export const heartbeatRequestSchema = z.object({
  appVersionCode: z.number().int().nonnegative(),
  appVersionName: z.string().max(40).default(''),
  /** What the device holds now; the server answers with what it should hold. */
  manifestVersion: z.string().nullable().default(null),
  playbackState: playbackStateSchema,
  currentAssetId: z.string().nullable().default(null),
  currentPlaylistId: z.string().nullable().default(null),
  storageFreeBytes: z.number().int().nonnegative(),
  storageUsedBytes: z.number().int().nonnegative(),
  uptimeSec: z.number().int().nonnegative(),
  /** Device wall clock, so the server can measure skew and the device correct itself. */
  deviceClockMs: z.number().int(),
  /**
   * Self-check of the one-time ADB grants. These are wiped by a reinstall, and
   * without them the app stops auto-starting and cannot stage its own updates —
   * so a device that has lost them needs to show up on the dashboard rather than
   * fail silently at the next reboot.
   */
  grants: z
    .object({
      overlay: z.boolean(),
      installPackages: z.boolean(),
    })
    .default({ overlay: false, installPackages: false }),
  updateState: updateStateSchema.default('none'),
  errors: z.array(deviceErrorSchema).max(20).default([]),
});

export const commandTypeSchema = z.enum([
  'reload',
  'restart_app',
  'clear_cache',
  'apply_update',
]);

export const deviceCommandSchema = z.object({
  id: z.string(),
  type: commandTypeSchema,
  payload: z.record(z.string(), z.unknown()).default({}),
  issuedAt: z.string().datetime(),
});

export const heartbeatResponseSchema = z.object({
  manifestVersion: z.string(),
  commands: z.array(deviceCommandSchema).default([]),
  serverTimeMs: z.number().int(),
});

export const commandAckSchema = z.object({
  commandId: z.string().min(1),
  status: z.enum(['done', 'failed']),
  detail: z.string().max(500).default(''),
});

export const playbackEventSchema = z.object({
  assetId: z.string().min(1),
  playlistId: z.string().nullable().default(null),
  startedAt: z.string().datetime(),
  durationMs: z.number().int().nonnegative(),
  completed: z.boolean(),
});

export const playbackBatchSchema = z.object({
  events: z.array(playbackEventSchema).max(1000),
});

// ---------------------------------------------------------------------------
// Admin: stores and screens
// ---------------------------------------------------------------------------

export const brandSchema = z.string().min(1).max(40);

export const timezoneSchema = z
  .string()
  .refine(isValidTimeZone, { message: 'Unknown IANA timezone' });

/**
 * The hour of the day this store is willing to be interrupted by an app update.
 *
 * An update needs somebody in the shop to press a button on the remote, so the
 * confirmation dialog necessarily covers the menu board while it is up. This is where
 * that is allowed to happen. `null` means no window is set and an update may only be
 * sent with an explicit "now".
 *
 * Same shape and same rules as `openingHoursEntrySchema`: `"HH:MM"`, start inclusive,
 * end exclusive, and an end at or before the start wraps past midnight — so 22:30–00:30
 * is a legal late-night window rather than an error. Evaluated in the **store's** own
 * timezone, never the server's or the browser's.
 *
 * Choose a window that is still inside trading hours. The offline alerter deliberately
 * says nothing about a screen that is not expected to be up, so an update applied after
 * close produces no alert and a dashboard badge indistinguishable from a normally dark
 * television — the failure would be found by staff in the morning rather than by you.
 */
export const updateWindowSchema = z.object({
  start: hhMmSchema,
  end: hhMmSchema,
});

export const storeUpsertSchema = z.object({
  name: z.string().min(1).max(120),
  brand: brandSchema,
  timezone: timezoneSchema,
  address: z.string().max(300).default(''),
  openingHours: z.array(openingHoursEntrySchema).default([]),
  /** When a windowed update may prompt. Null means "only ever on an explicit now". */
  updateWindow: updateWindowSchema.nullable().default(null),
});

export const screenClaimSchema = z.object({
  userCode: z.string().min(1),
  storeId: z.string().min(1),
  name: z.string().min(1).max(120),
  sizeInches: z.number().positive().max(200).nullable().default(null),
  rotationDegrees: rotationDegreesSchema.default(0),
});

export const screenUpdateSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  sizeInches: z.number().positive().max(200).nullable().optional(),
  rotationDegrees: rotationDegreesSchema.optional(),
  /** See `manifestScreenSchema.autoStart`. Absent means "leave it alone". */
  autoStart: z.boolean().optional(),
  /**
   * Whether this screen ignores its store's opening hours and plays around the
   * clock. Absent means "leave it alone".
   *
   * It belongs to the screen and not to the store because the two really are
   * different televisions: a window screen advertises to the street at three in
   * the morning while the menu board above the counter goes dark with the shop,
   * and both are in the same shop with one set of opening hours.
   *
   * **The device is never told about this flag.** It is expressed to the device
   * as an empty `openingHours`, which the contract already defines as "runs
   * around the clock", so no manifest field, no resolver change and no APK
   * update are involved. See `assembleManifest` in `apps/api/src/manifest/build.ts`.
   */
  alwaysOn: z.boolean().optional(),
});

export const screenScheduleSchema = z.object({
  defaultPlaylistId: z.string().nullable().default(null),
  fallbackAssetId: z.string().nullable().default(null),
  rules: z
    .array(
      z.object({
        id: z.string().min(1),
        priority: z.number().int().default(10),
        days: z.array(z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5), z.literal(6), z.literal(7)])).min(1),
        start: z.string(),
        end: z.string(),
        playlistId: z.string().min(1),
      }),
    )
    .default([]),
});

// ---------------------------------------------------------------------------
// Admin: media and playlists
// ---------------------------------------------------------------------------

export const mediaScopeSchema = z.object({
  /** null means the shared, cross-store library. */
  storeId: z.string().nullable().default(null),
  /** Shared media only: limits which brands see it. Empty means every brand. */
  brands: z.array(brandSchema).default([]),
});

export const uploadInitSchema = z.object({
  fileName: z.string().min(1).max(260),
  contentType: z.string().min(1).max(100),
  bytes: z.number().int().positive(),
  title: z.string().max(200).default(''),
  scope: mediaScopeSchema,
  /** Read client-side before upload; the server re-checks these at completion. */
  probe: z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    durationMs: z.number().int().positive().nullable().default(null),
    videoCodec: z.string().max(40).nullable().default(null),
    rotationMetadataDegrees: z.number().int().nullable().default(null),
  }),
});

export const uploadInitResponseSchema = z.object({
  assetId: z.string(),
  uploadId: z.string(),
  partSize: z.number().int().positive(),
  partCount: z.number().int().positive(),
  thumbPutUrl: z.string().url(),
});

export const uploadPartUrlsRequestSchema = z.object({
  partNumbers: z.array(z.number().int().positive().max(10_000)).min(1).max(20),
});

export const uploadPartUrlsResponseSchema = z.object({
  urls: z.array(z.object({ partNumber: z.number().int().positive(), url: z.string().url() })),
  expiresAt: z.string().datetime(),
});

export const uploadCompleteSchema = z.object({
  /** Optional: the server can list parts itself if the browser could not read ETags. */
  parts: z
    .array(z.object({ partNumber: z.number().int().positive(), eTag: z.string().min(1) }))
    .optional(),
});

export const mediaUpdateSchema = z.object({
  title: z.string().max(200).optional(),
  brands: z.array(brandSchema).optional(),
  contentExpiresAt: z.string().datetime().nullable().optional(),
});

export const playlistUpsertSchema = z.object({
  name: z.string().min(1).max(120),
  storeId: z.string().nullable().default(null),
  brands: z.array(brandSchema).default([]),
  items: z.array(playlistItemSchema).max(200).default([]),
});

// ---------------------------------------------------------------------------
// Admin: commands, overrides, releases
// ---------------------------------------------------------------------------

export const issueCommandSchema = z.object({
  type: commandTypeSchema,
  payload: z.record(z.string(), z.unknown()).default({}),
  /**
   * `now` hands the command to the device on its next heartbeat, about a minute.
   * `window` holds it until the store's `updateWindow` comes round, so an update
   * cannot put a dialog over the menu board in the middle of a lunch rush.
   *
   * The default is `now` because that is what every existing command means and what
   * an un-updated caller expects. The panel defaults an `apply_update` to `window`
   * when the store has one, which is where the safety actually belongs — a default
   * here would silently delay a `restart_app` somebody needed immediately.
   */
  deliver: z.enum(['now', 'window']).default('now'),
});

export const overrideUpsertSchema = z
  .object({
    playlistId: z.string().min(1).nullable().default(null),
    assetId: z.string().min(1).nullable().default(null),
    startsAt: z.string().datetime().nullable().default(null),
    endsAt: z.string().datetime(),
  })
  .refine((value) => (value.playlistId === null) !== (value.assetId === null), {
    message: 'An override names either a playlist or a single asset, not both',
  });

export const releaseCreateSchema = z.object({
  versionCode: z.number().int().positive(),
  versionName: z.string().min(1).max(40),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  bytes: z.number().int().positive(),
  force: z.boolean().default(false),
  notes: z.string().max(2000).default(''),
});

// ---------------------------------------------------------------------------
// Fleet view
// ---------------------------------------------------------------------------

export const screenHealthSchema = z.enum(['online', 'offline', 'closed', 'never-seen']);

export const fleetScreenSchema = z.object({
  screen: manifestScreenSchema,
  storeId: z.string(),
  storeName: z.string(),
  health: screenHealthSchema,
  /**
   * Whether this screen ignores its store's opening hours — see
   * `screenUpdateSchema.alwaysOn`.
   *
   * A sibling of `screen` rather than a member of it, because `screen` is
   * `manifestScreenSchema`: the shape the *device* is sent. The device is never
   * sent this, so putting it there would be a contract change for a setting the
   * contract deliberately does not have.
   *
   * Defaulted rather than required so the panel still parses a response from an
   * API deployed before this field existed — which, given the deploy order is
   * shared then API then panel, is a state the fleet page will genuinely be in.
   */
  alwaysOn: z.boolean().default(false),
  lastHeartbeatAt: z.string().datetime().nullable(),
  playbackState: playbackStateSchema.nullable(),
  currentAssetId: z.string().nullable(),
  appVersionCode: z.number().int().nullable(),
  updateState: updateStateSchema.nullable(),
  storageFreeBytes: z.number().int().nullable(),
  clockSkewMs: z.number().int().nullable(),
  grantsOk: z.boolean().nullable(),
  errorCount: z.number().int().nonnegative(),
  /**
   * What this screen is set to play, or null if nothing has been assigned to it.
   *
   * On the fleet row rather than only on the detail, because a screen with no
   * playlist is the single most common reason a television shows nothing, and
   * without this the panel cannot see the difference between a screen that is
   * working and one that has never been pointed at anything. It reads as a
   * healthy, online, idle screen — which is exactly how it looks to somebody
   * standing in front of it wondering what is broken.
   */
  defaultPlaylistId: z.string().nullable().default(null),
});

export type UserRole = z.infer<typeof userRoleSchema>;
export type SessionUser = z.infer<typeof sessionUserSchema>;
export type DeviceInfo = z.infer<typeof deviceInfoSchema>;
export type PairInitResponse = z.infer<typeof pairInitResponseSchema>;
export type PairPollResponse = z.infer<typeof pairPollResponseSchema>;
export type PairPollPendingResponse = z.infer<typeof pairPollPendingResponseSchema>;
export type HeartbeatRequest = z.infer<typeof heartbeatRequestSchema>;
export type HeartbeatResponse = z.infer<typeof heartbeatResponseSchema>;
export type DeviceCommandDto = z.infer<typeof deviceCommandSchema>;
export type CommandType = z.infer<typeof commandTypeSchema>;
export type DeviceErrorCode = z.infer<typeof deviceErrorCodeSchema>;
export type DeviceErrorDto = z.infer<typeof deviceErrorSchema>;
export type PlaybackState = z.infer<typeof playbackStateSchema>;
export type UpdateState = z.infer<typeof updateStateSchema>;
export type PlaybackEvent = z.infer<typeof playbackEventSchema>;
export type UpdateWindow = z.infer<typeof updateWindowSchema>;
export type StoreUpsert = z.infer<typeof storeUpsertSchema>;
export type ScreenClaim = z.infer<typeof screenClaimSchema>;
export type ScreenSchedule = z.infer<typeof screenScheduleSchema>;
export type UploadInit = z.infer<typeof uploadInitSchema>;
export type UploadInitResponse = z.infer<typeof uploadInitResponseSchema>;
export type PlaylistUpsert = z.infer<typeof playlistUpsertSchema>;
export type OverrideUpsert = z.infer<typeof overrideUpsertSchema>;
export type ReleaseCreate = z.infer<typeof releaseCreateSchema>;
export type FleetScreen = z.infer<typeof fleetScreenSchema>;
export type ScreenHealth = z.infer<typeof screenHealthSchema>;
